import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { ButtonProps } from '@innovamat/glimmer-components';
import {
  Modal,
  RadioButton,
  TableTag,
  Typography,
  getTypography,
  snack,
} from '@innovamat/glimmer-components';
import type { ClassroomBody } from '@innovamat/glow-api-client';
import {
  useClassroomQuery,
  useOrganizationQuery,
  useStudentsQuery,
} from '@innovamat/glow-api-client';

import { useClassroom } from '../../hooks/use-classroom';
import {
  SCHEDULE_DEFAULT_VALUES,
  useClassroomScheduleValues,
} from '../../hooks/use-classroom-schedule-values';
import { usePutClassroom } from '../../hooks/use-put-classroom';
import {
  DIGITAL_PRACTICE_ACCESS,
  isHomeAccess,
  isNoAccess,
  type DigitalPracticeAccess,
} from '../../modules/classroom/classroom-home-access';
import { ClassroomSchedule } from '../classroom-schedule';

type HomeAccessModalProps = {
  isOpen: boolean;
  onClose: (accessType?: string) => void;
  isDisclaimer?: boolean;
  classroomId: string;
  organizationId: string;
};
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ContentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const HorizontalContent = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const SubtleText = styled(Typography.Body2)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

const RadioButtonDescription = styled.ul`
  ${({ theme }) => getTypography(theme, 'Body 2')};
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
  margin-left: 2.5rem;

  li {
    padding-left: 0.25rem;
  }

  li::marker {
    color: ${({ theme }) =>
      theme.tokens.color.alias.cm.text['text-subtle'].value};
    font-size: 0.75rem;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export function HomeAccessModal({
  isOpen,
  onClose,
  isDisclaimer,
  classroomId,
  organizationId,
}: HomeAccessModalProps): JSX.Element {
  const schedule = useClassroomScheduleValues();
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const queryClient = useQueryClient();
  const [isInternalDisclaimer, setIsInternalDisclaimer] =
    useState(isDisclaimer);
  const [invalidatingCache, setInvalidatingCache] = useState(false);
  const { t } = useTranslation();

  const handleOnSuccess = async () => {
    if (isScheduleOpen) {
      setIsScheduleOpen(false);
      snack.success(t('common.toast.success'));
    } else {
      setInvalidatingCache(true);
      await queryClient.refetchQueries({
        queryKey: useStudentsQuery.getKey({ classroomId, organizationId }),
      });
      await queryClient.refetchQueries({
        queryKey: useClassroomQuery.getKey({
          id: classroomId,
        }),
      });
      snack.success(t('common.toast.success'));
      setInvalidatingCache(false);
      onClose(homeAccessMode);
    }
  };

  const handleOnError = (): void => {
    snack.error(t('common.toast.error'));
    onClose();
  };

  const handleOnSend = (): void => {
    if (!classroom) return;
    const body: ClassroomBody = {
      id: classroom.id,
      icon: classroom.icon,
      organizationId,
      level: classroom.level,
      name: classroom.name,
      courseId: classroom.courseId,
      avatar: classroom.avatar,
      scheduleEnd: schedule.values.scheduleEnd,
      scheduleStart: schedule.values.scheduleStart,
      type: classroom.type,
    };

    const updatedHomeAccessMode =
      classroom?.digitalPracticeAccess !== homeAccessMode
        ? homeAccessMode
        : undefined;

    const propagateStudents = isNoAccess(homeAccessMode);

    handlePutClassroom(
      body,
      schedule.values.timeZone!,
      updatedHomeAccessMode,
      propagateStudents
    );
  };

  const { handlePutClassroom, isPending } = usePutClassroom({
    onSuccess: handleOnSuccess,
    onError: handleOnError,
  });

  const { data: organizationQuery } = useOrganizationQuery(
    { id: organizationId },
    { staleTime: Infinity, enabled: !!organizationId }
  );

  const { classroom } = useClassroom({
    classroomId,
  });

  const [homeAccessMode, setHomeAccessMode] = useState<DigitalPracticeAccess>(
    (classroom?.digitalPracticeAccess as DigitalPracticeAccess) ||
      DIGITAL_PRACTICE_ACCESS.NO_ACCESS
  );

  const initScheduleValues = () => {
    const timezone = organizationQuery?.organization?.timezone || '';
    schedule.handleInitValues({
      scheduleStart:
        classroom?.scheduleStart || SCHEDULE_DEFAULT_VALUES.scheduleStart,
      scheduleEnd:
        classroom?.scheduleEnd || SCHEDULE_DEFAULT_VALUES.scheduleEnd,
      timeZone: timezone,
    });
  };

  useEffect(() => {
    initScheduleValues();

    setHomeAccessMode(
      (classroom?.digitalPracticeAccess as DigitalPracticeAccess) ||
        DIGITAL_PRACTICE_ACCESS.NO_ACCESS
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classroom, organizationQuery]);

  useEffect(() => {
    setIsInternalDisclaimer(isDisclaimer);
  }, [isOpen, isDisclaimer]);

  const getButtons = (): ButtonProps[] => {
    const isLoadingOrInvalidating = isPending || invalidatingCache;

    if (isInternalDisclaimer) {
      return [
        {
          children: t('students.home-access-modal.configure'),
          variant: 'accent',
          onClick: () => {
            setIsInternalDisclaimer(false);
          },
        },
        {
          children: t('Settings.Cancel'),
          variant: 'tertiary',
          onClick: () => onClose(),
        },
      ];
    }

    return [
      {
        children: t('Settings.SaveChanges'),
        variant: 'accent',
        loading: isScheduleOpen ? false : isLoadingOrInvalidating,
        onClick: handleOnSend,
      },
      {
        children: t('Settings.Cancel'),
        variant: 'tertiary',
        disabled: isScheduleOpen ? false : isLoadingOrInvalidating,
        onClick: () => onClose(),
      },
    ];
  };

  const getScheduleButtons = (): ButtonProps[] => {
    return [
      {
        children: t('Settings.SaveChanges'),
        variant: 'accent',
        loading: isPending,
        onClick: handleOnSend,
      },
      {
        children: t('Settings.Cancel'),
        variant: 'tertiary',
        disabled: isPending,
        onClick: () => {
          initScheduleValues();
          setIsScheduleOpen(false);
        },
      },
    ];
  };

  return (
    <Modal
      isOpen={isOpen}
      title={t('students.home-access-modal.title')}
      onClose={onClose}
      buttons={getButtons()}
      modalWidth={692}
    >
      <>
        <Modal
          isOpen={isScheduleOpen}
          title={t('students.schedule-modal.title')}
          onClose={() => setIsScheduleOpen(false)}
          buttons={getScheduleButtons()}
          modalWidth={692}
        >
          <ClassroomSchedule {...schedule} disableTimeZoneSelector />
        </Modal>
        {isInternalDisclaimer ? (
          t('students.home-access-modal.disclaimer')
        ) : (
          <ContentContainer>
            <HorizontalContent>
              <Typography.Subtitle1>
                {t('classroom.schedule-time.title')}
              </Typography.Subtitle1>
              <SubtleText>{schedule.startAndEndString}</SubtleText>
              <Typography.Link2
                onClick={() => {
                  setIsScheduleOpen(true);
                }}
              >
                {t('common.change')}
              </Typography.Link2>
            </HorizontalContent>
            <ContentBlock>
              <Typography.Subtitle1>
                {t('students.home-access-modal.digital-practice-mode.subtitle')}
              </Typography.Subtitle1>
              <FlexContainer>
                <RadioButton
                  groupBy="access-0"
                  label={t('students.home-access-modal.no-access')}
                  value={DIGITAL_PRACTICE_ACCESS.NO_ACCESS}
                  checked={isNoAccess(homeAccessMode)}
                  onClick={() => {
                    setHomeAccessMode(DIGITAL_PRACTICE_ACCESS.NO_ACCESS);
                  }}
                />
                <TableTag type="accent" text={t('recommended-tag')} />
              </FlexContainer>

              <RadioButtonDescription>
                <Trans
                  i18nKey="students.home-access-modal.home-training.description"
                  components={{
                    li: <li />,
                  }}
                />
              </RadioButtonDescription>
              <RadioButton
                groupBy="access-2"
                label={t('students.home-access-modal.home-access')}
                value={DIGITAL_PRACTICE_ACCESS.HOME_ACCESS}
                checked={isHomeAccess(homeAccessMode)}
                onClick={() => {
                  setHomeAccessMode(DIGITAL_PRACTICE_ACCESS.HOME_ACCESS);
                }}
              />

              <RadioButtonDescription>
                <Trans
                  i18nKey="students.home-access-modal.home-access.description"
                  components={{
                    li: <li />,
                  }}
                />
              </RadioButtonDescription>
            </ContentBlock>
          </ContentContainer>
        )}
      </>
    </Modal>
  );
}
